// Time-bucketed histogram of per-endpoint timestamps for the Inventory
// Surface's "First seen" / "Last seen" tabs. Pure + node-tested, mirroring
// inventoryDistribution.js (which shapes the categorical composition tabs).
//
// Input is ONE epoch-ms timestamp per endpoint, both now sourced fleet-wide from
// the same analytics change-log aggregate (scoped client-side):
//   • first-seen = earliest observed metadata change (MIN(change_time), a lossy
//     enrollment proxy — see executeFirstSeenByEndpoint in apiClient.js);
//   • last-seen  = most recent heartbeat (MAX(received_at) over the endpoint
//     status entity — see executeLastSeenByEndpoint; a true last-contact, NOT
//     refined by the loaded rows' stale metadata snapshotTime).
//
// bucketBy() groups them into a contiguous spine at the requested granularity
// (day | week | month, UTC, deterministic) across the data's own span —
// trunc(min) .. trunc(max) inclusive, empty interior buckets kept as zero bars.
// Each bucket carries the per-period `count` AND the running `cumulative`: the
// first-seen chart draws bars (count) + a rollout line (cumulative); the
// last-seen chart draws bars only. Label formatting lives in SeenChart (it has
// the granularity), so buckets carry only `startMs` + the two counts.

const DAY_MS = 86400000;
export const GRANULARITIES = ["day", "week", "month"];

// Defensive ceiling on the contiguous spine so a stray ancient/garbage
// timestamp can't allocate a runaway array. Within the 365-day change-log TTL,
// day-granularity is ≤366 buckets, so this never bites real data.
const MAX_BUCKETS = 1200;

function truncUTC(ms, g) {
  const d = new Date(ms);
  const y = d.getUTCFullYear();
  const mo = d.getUTCMonth();
  if (g === "month") return Date.UTC(y, mo, 1);
  const dayStart = Date.UTC(y, mo, d.getUTCDate());
  if (g === "week") {
    const dow = (d.getUTCDay() + 6) % 7; // Monday = 0
    return dayStart - dow * DAY_MS;
  }
  return dayStart; // day
}

function nextUTC(ms, g) {
  if (g === "month") {
    const d = new Date(ms);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1); // month 12 → next Jan
  }
  return ms + (g === "week" ? 7 * DAY_MS : DAY_MS);
}

// Whole-bucket offset of a (truncated) timestamp from the spine start.
function bucketIndex(startMs, truncMs, g) {
  if (g === "month") {
    const a = new Date(startMs);
    const b = new Date(truncMs);
    return (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + (b.getUTCMonth() - a.getUTCMonth());
  }
  return Math.round((truncMs - startMs) / (g === "week" ? 7 * DAY_MS : DAY_MS));
}

export function bucketBy(timestampsMs = [], granularity = "month") {
  const g = GRANULARITIES.includes(granularity) ? granularity : "month";
  const ts = timestampsMs.filter((m) => Number.isFinite(m)).sort((a, b) => a - b);
  if (!ts.length) {
    return { buckets: [], total: 0, maxCount: 0, maxCumulative: 0, granularity: g, firstMs: null, lastMs: null };
  }
  const firstMs = ts[0];
  const lastMs = ts[ts.length - 1];

  const startMs = truncUTC(firstMs, g);
  const endMs = truncUTC(lastMs, g);
  const buckets = [];
  for (let m = startMs; m <= endMs && buckets.length < MAX_BUCKETS; m = nextUTC(m, g)) {
    buckets.push({ startMs: m, count: 0, cumulative: 0 });
  }

  for (const t of ts) {
    const i = bucketIndex(startMs, truncUTC(t, g), g);
    if (i >= 0 && i < buckets.length) buckets[i].count += 1;
  }

  let run = 0;
  let maxCount = 0;
  for (const b of buckets) {
    run += b.count;
    b.cumulative = run;
    if (b.count > maxCount) maxCount = b.count;
  }
  return { buckets, total: ts.length, maxCount, maxCumulative: run, granularity: g, firstMs, lastMs };
}

// Membership test against an in-scope endpoint-id Set. A null/undefined set means
// "no scope filter" (the whole fleet) — passed at the global/root scope so the
// seen charts bucket every fleet endpoint the analytics aggregate returns, not a
// truncated id list. Endpoint ids share one encoding across the analytics UUID
// cells and flattenInventoryRow.id, so a plain Set check scopes both sides.
function inScope(scopeIds, id) {
  return scopeIds ? scopeIds.has(id) : true;
}

// Per-endpoint last-contact map for the in-scope fleet, straight from the
// whole-fleet heartbeat aggregate (executeLastSeenByEndpoint →
// [{ id, lastSeenMs }], MAX(received_at)). It deliberately does NOT fall back to
// the loaded rows' snapshotTime: that field is the change-triggered metadata
// last_updated — the stale proxy this repoint exists to replace — so mixing it
// back in would both reintroduce the staleness and re-couple the chart to the
// capped client row set. If the heartbeat aggregate is unavailable (e.g. before
// backend #2162 deploys ENTITY_TYPE_ENDPOINT_STATUS), last-seen shows empty
// rather than silently rendering the old proxy. Scoped by `scopeIds` (a Set, or
// null = whole fleet).
function lastContactById(lastByEndpoint = [], scopeIds = null) {
  const byId = new Map();
  for (const e of lastByEndpoint || []) {
    const id = e?.id;
    const ms = e?.lastSeenMs;
    if (!id || !Number.isFinite(ms) || !inScope(scopeIds, id)) continue;
    const prev = byId.get(id);
    if (prev === undefined || ms > prev) byId.set(id, ms);
  }
  return byId;
}

// One-per-endpoint last-seen timestamps for the in-scope fleet — the values of
// lastContactById (heartbeat aggregate only; see there).
export function scopedLastSeenTimestamps(lastByEndpoint = [], scopeIds = null) {
  return Array.from(lastContactById(lastByEndpoint, scopeIds).values());
}

// Whole-fleet first-seen records (executeFirstSeenByEndpoint → [{ id, firstSeenMs }])
// scoped to the in-scope endpoint set. Previously intersected with the LOADED rows,
// which capped the rollout at however many inventory rows had paged in; now scoped by
// the full in-scope id set (`scopeIds` Set, or null = whole fleet) so it reflects the
// fleet, not the table's current page (O-2696).
export function scopedFirstSeenTimestamps(byEndpoint = [], scopeIds = null) {
  const out = [];
  for (const e of byEndpoint || []) {
    if (e && inScope(scopeIds, e.id) && Number.isFinite(e.firstSeenMs)) out.push(e.firstSeenMs);
  }
  return out;
}

// Per-endpoint [first contact, last contact] spans for the "Under management"
// occupancy view. Both edges come from the analytics aggregates: last contact =
// the heartbeat aggregate (lastContactById, MAX(received_at)); first contact =
// the change-log first-seen proxy joined by id when known, else the last contact
// (a point) so an endpoint without first-seen history still shows up. Scoped by
// `scopeIds` (a Set, or null = whole fleet).
export function endpointIntervals(firstByEndpoint = [], lastByEndpoint = [], scopeIds = null) {
  const firstById = new Map();
  for (const e of firstByEndpoint || []) {
    if (e && inScope(scopeIds, e.id) && Number.isFinite(e.firstSeenMs)) firstById.set(e.id, e.firstSeenMs);
  }
  const out = [];
  for (const [id, lastMs] of lastContactById(lastByEndpoint, scopeIds)) {
    const firstRaw = firstById.get(id);
    const firstMs = Number.isFinite(firstRaw) ? Math.min(firstRaw, lastMs) : lastMs;
    out.push({ firstMs, lastMs });
  }
  return out;
}

// Occupancy histogram: count an endpoint in EVERY bucket its [firstMs, lastMs]
// span covers (a difference-array sweep — O(intervals + buckets)). Unlike
// bucketBy (one bucket per endpoint), this shows the live fleet over time —
// rising as endpoints enroll, falling as they go quiet. `count` is the occupancy
// at each bucket; there is no running cumulative (the bar height IS the value).
export function bucketPresence(intervals = [], granularity = "month") {
  const g = GRANULARITIES.includes(granularity) ? granularity : "month";
  const spans = intervals.filter((iv) => iv && Number.isFinite(iv.firstMs) && Number.isFinite(iv.lastMs) && iv.firstMs <= iv.lastMs);
  if (!spans.length) {
    return { buckets: [], total: 0, maxCount: 0, maxCumulative: 0, granularity: g, firstMs: null, lastMs: null };
  }
  let lo = Infinity;
  let hi = -Infinity;
  for (const iv of spans) {
    if (iv.firstMs < lo) lo = iv.firstMs;
    if (iv.lastMs > hi) hi = iv.lastMs;
  }
  const startMs = truncUTC(lo, g);
  const endMs = truncUTC(hi, g);
  const starts = [];
  for (let m = startMs; m <= endMs && starts.length < MAX_BUCKETS; m = nextUTC(m, g)) starts.push(m);
  const n = starts.length;

  // diff[i] enters at the span's first bucket, leaves after its last bucket;
  // the prefix sum is the occupancy in each bucket.
  const diff = new Array(n + 1).fill(0);
  for (const iv of spans) {
    let s = bucketIndex(startMs, truncUTC(iv.firstMs, g), g);
    let e = bucketIndex(startMs, truncUTC(iv.lastMs, g), g);
    if (s < 0) s = 0;
    if (e > n - 1) e = n - 1;
    if (e < s) continue;
    diff[s] += 1;
    diff[e + 1] -= 1;
  }
  const buckets = [];
  let run = 0;
  let maxCount = 0;
  for (let i = 0; i < n; i++) {
    run += diff[i];
    if (run > maxCount) maxCount = run;
    buckets.push({ startMs: starts[i], count: run, cumulative: run });
  }
  return { buckets, total: spans.length, maxCount, maxCumulative: maxCount, granularity: g, firstMs: lo, lastMs: hi };
}
